import { flow } from 'lodash/fp';
import { toCamel } from '@/components/utils/core/word';
import { SEARCH_CATEGORY_SLUGS_MAP, CONTENT_CATEGORY_SLUGS } from '@/components/common/UniversalSearch/constants';
import {
  Author,
  Company,
  CompanyPagesCatalog,
  CompanyPagesCatalogPageType,
  SearchResultCategory,
} from '@/types/graphql';
import type { UniversalSearchResultItem } from '@/components/common/UniversalSearch/types';
import type { UserAuthor } from '@/types/graphql';

type ItemData = {
  id: string;
  slug: string;
  category: string;
  link: string;
  isExternal?: boolean;
  section?: SearchResultCategory;
};

export const buildSearchItemData = (item: UniversalSearchResultItem): ItemData | undefined => {
  switch (item.__typename) {
    case 'AcademyArticleSearchResult':
      return {
        id: String(item.article.id),
        slug: item.article.slug,
        category: item.category,
        link: `/academy/${item.article.slug}`,
        section: SearchResultCategory.AcademyArticles,
      };
    case 'CompanySearchResult':
      return {
        id: item.company.id,
        slug: item.company.slug ?? '',
        category: item.category,
        link: `/salaries/company/${item.company.slug}/`,
        isExternal: true,
        section: SearchResultCategory.Companies,
      };
    case 'PortfolioUserSearchResult':
      return {
        id: item.portfolioUser.slug,
        slug: item.portfolioUser.slug,
        category: item.category,
        link: `/p/${item.portfolioUser.slug}`,
        section: SearchResultCategory.UserPortfolios,
      };
    case 'WarRoomPostSearchResult':
      return {
        id: item.post.id,
        slug: item.post.slug,
        category: item.category,
        link: `/war-room/posts/${item.post.slug}`,
        section: SearchResultCategory.WrPosts,
      };
    case 'WarRoomUserSearchResult':
      return {
        id: String(item.warRoomUser?.id),
        slug: String(item.warRoomUser?.username),
        category: item.category,
        link: `/war-room/profile/${item.warRoomUser?.username}`,
        section: SearchResultCategory.WrUsers,
      };
    case 'WarRoomPostCommentSearchResult':
      return {
        id: String(item.record?.id),
        slug: String(item.record?.id),
        category: item.category,
        link: commentLink(item.record?.post?.slug, item.record?.id),
        section: SearchResultCategory.WrComments,
      };
    case 'CompanyPagesCatalogSearchResult':
      let link = CompanyPagesCatalogPageType.Compensation
        ? compensationPagesLink(item.page)
        : companyPagesLink(item.page);
      return {
        id: String(item.page?.id),
        slug: String(item.page?.id),
        category: item.category,
        link: link,
        section: SearchResultCategory.CompanyPagesCatalogs,
      };
  }
};

export const commentLink = (slug: string, id: string) => `/war-room/posts/${slug}/comments/${id}`;

export const companyPagesLink = (companyPagesCatalog: CompanyPagesCatalog) => {
  let pageType = companyPagesCatalog.pageType;
  let company = companyPagesCatalog.company?.slug;
  let role = companyPagesCatalog.roleCategory?.slug;
  let url = '/salaries';

  if (company) url += `/company/${company}`;

  switch (pageType) {
    case CompanyPagesCatalogPageType.Salaries:
      url += '/salaries';
      if (role) url += `/${role}`;
      break;
    case CompanyPagesCatalogPageType.Reviews:
      url += '/reviews';
      break;
    case CompanyPagesCatalogPageType.Interviews:
      url += '/interviews';
      break;
  }

  return url;
};

export const compensationPagesLink = (companyPagesCatalog: CompanyPagesCatalog) => {
  let role = companyPagesCatalog.roleCategory?.slug;
  let accountSize = companyPagesCatalog.customerSegment?.slug;
  let region = companyPagesCatalog.region?.slug;
  let url = '/salaries/compensation';

  if (accountSize) url += `/${accountSize}-${role}/`;
  else if (region) url += `/${role}/${region}`;
  else url += `/${role}`;

  return url;
};

export const displayCompanyEmployees = (value: string) =>
  value
    .split('-')
    .map(count => {
      if (count.length >= 4) {
        // Transform 1000 to 1k and 1000+ to 1k+
        return count.endsWith('+') ? `${count.slice(0, -4)}k+` : `${count.slice(0, -3)}k`;
      }
      return count;
    })
    .join('-');

export const getAuthorDetails = (author: UserAuthor | Author) => {
  let initials = '';
  let username = '';
  let avatar = '';
  let isAnonymous = false;
  let link = '';
  let isPublic = true;

  if (author.__typename === 'Public') {
    initials = author.firstname?.substring(0, 2) ?? '';
    avatar = String(author.avatarUrl);
    username = `${author.firstname} ${author.lastname}`.trim();
    link = `/p/${author.slug}`;
    isPublic = Boolean(author.portfolioIsOpened);
  } else if (author.__typename === 'Anonymous') {
    initials = author.username?.substring(0, 2) ?? '';
    avatar = String(author.warRoomAvatarUrl);
    username = author.username ?? '';
    isAnonymous = true;
    link = `/war-room/profile/${username}`;
  } else if (author.__typename === 'Author') {
    username = author.name ?? '';
    initials = username.substring(0, 2) ?? '';
    avatar = String(author.avatar?.url);
    link = author.linkUrl ?? '';
    isAnonymous = true;
  }

  return { initials, username, avatar, isAnonymous, link, isPublic };
};

export const getSearchCategoryBySlug = (slug: string) => {
  for (let [key, value] of SEARCH_CATEGORY_SLUGS_MAP.entries()) {
    if (slug === value) {
      return CONTENT_CATEGORY_SLUGS.includes(key) ? SearchResultCategory.Content : key;
    }
  }
};

export const getSlugBySearchCategory = (category: SearchResultCategory) => SEARCH_CATEGORY_SLUGS_MAP.get(category);

export const categoryToPaginationKey = (category: string): string => toCamel(category.toLowerCase());

// parse and insert links
const linkify = (text: string): string =>
  text.replace(
    /(<a href=")?((https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)))(">(.*)<\/a>)?/gi,
    (...args) => {
      const url = args[2];
      const isInternal = url?.includes(window.location.host);
      // FIXME: quick fix for ended dot in link, better to rewrite regexp or replace with simpler
      return `<a href="${url.replace(/\.$/, '')}" target="_blank"${
        !isInternal ? ' rel="nofollow noopener noreferrer"' : ''
      }>${args[7] || url}</a>`;
    },
  );

const insertProfile = (text: string): string => {
  const host = window.location.origin;
  return text.replace(/(^|\s)@(?!_)[a-z0-9_\-\.]+/gi, (...args) => {
    const profile = (args[2] ?? '').trim();
    return `<a href="${host}/war-room/profile/${profile.slice(1)}" target="_blank">${profile}</a>`;
  });
};

const insertEmail = (text: string): string => {
  return text.replace(
    /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/,
    (...args) => {
      const email = args[0];
      return `<a href="mailto:${email}">${email}</a>`;
    },
  );
};

// remove starting of html tag if it doesn't close to prevent < & </ in text during the streaming
const fixHtml = (text: string): string => text.replace(/<[^>]*$/g, '');

export const textSteamToHtml = flow([linkify, insertProfile, insertEmail, fixHtml]);
